import React, { useState } from 'react';
import { PlusIcon, TrashIcon, ArrowLeftIcon, UserPlusIcon, MailIcon, BuildingIcon, GlobeIcon, SaveIcon, CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, BarChartIcon, CreditCardIcon } from 'lucide-react';
import { FileUpload } from '../FileUpload';
export function VendorOnboarding({
  onSave,
  onSendInvite,
  setCurrentView,
  entities = [],
  locale = 'en-US'
}) {
  const [onboardingMethod, setOnboardingMethod] = useState('direct');
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: '',
    country: '',
    taxId: '',
    website: '',
    contactName: '',
    email: '',
    phone: '',
    role: '',
    // Address fields
    street: '',
    city: '',
    state: '',
    postalCode: '',
    addressCountry: '',
    addressType: 'Physical',
    isPrimary: true,
    // Entity and site restrictions
    isGlobal: true,
    entities: [],
    sites: [],
    // ESG data
    esg: {
      score: '',
      environmental: '',
      social: '',
      governance: '',
      summary: ''
    },
    // Bank account fields
    bankName: '',
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    swiftCode: '',
    iban: '',
    accountType: 'Checking'
  });
  const [inviteData, setInviteData] = useState({
    name: '',
    type: '',
    email: '',
    contactName: '',
    phone: '',
    role: '',
    message: 'We invite you to register as a vendor in our system. Please complete the onboarding process by following the link that will be sent to your email.'
  });
  const [errors, setErrors] = useState({});
  const [contacts, setContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [files, setFiles] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingBankAccount, setEditingBankAccount] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showBankAccountForm, setShowBankAccountForm] = useState(false);
  const [showESGForm, setShowESGForm] = useState(false);
  const [isESGEnabled] = useState(true); // In a real app, this would come from tenant settings
  const [hasESGEditPermission] = useState(true); // In a real app, this would come from user permissions
  const countries = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Japan', 'China', 'India'];
  const vendorTypes = ['Supplier', 'Manufacturer', 'Distributor', 'Service Provider', 'Consultant', 'Contractor', 'Other'];
  const addressTypes = ['Physical', 'Billing', 'Shipping', 'Mailing', 'Registered', 'Other'];
  const accountTypes = ['Checking', 'Savings', 'Current', 'Business', 'Corporate'];
  // Get available sites based on selected entities
  const getAvailableSites = () => {
    if (formData.isGlobal) return [];
    let availableSites = [];
    formData.entities.forEach(entityId => {
      const entity = entities.find(e => e.id === entityId);
      if (entity && entity.sites) {
        availableSites = [...availableSites, ...entity.sites];
      }
    });
    return availableSites;
  };
  // Handle entity selection
  const handleEntityChange = e => {
    const entityId = e.target.value;
    const isChecked = e.target.checked;
    if (isChecked) {
      setFormData({
        ...formData,
        entities: [...formData.entities, entityId]
      });
    } else {
      // Remove entity and any sites associated with this entity
      const entity = entities.find(e => e.id === entityId);
      const entitySiteIds = entity?.sites?.map(site => site.id) || [];
      setFormData({
        ...formData,
        entities: formData.entities.filter(id => id !== entityId),
        sites: formData.sites.filter(siteId => !entitySiteIds.includes(siteId))
      });
    }
  };
  // Handle site selection
  const handleSiteChange = e => {
    const siteId = e.target.value;
    const isChecked = e.target.checked;
    if (isChecked) {
      setFormData({
        ...formData,
        sites: [...formData.sites, siteId]
      });
    } else {
      setFormData({
        ...formData,
        sites: formData.sites.filter(id => id !== siteId)
      });
    }
  };
  // Handle availability type change
  const handleAvailabilityChange = isGlobal => {
    setFormData({
      ...formData,
      isGlobal,
      // Clear entity and site selections when switching to global
      entities: isGlobal ? [] : formData.entities,
      sites: isGlobal ? [] : formData.sites
    });
  };
  const handleInputChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    if (name.startsWith('esg.')) {
      const esgField = name.split('.')[1];
      setFormData({
        ...formData,
        esg: {
          ...formData.esg,
          [esgField]: value
        }
      });
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const handleInviteInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setInviteData({
      ...inviteData,
      [name]: value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const addContact = () => {
    if (!formData.contactName || !formData.email) {
      setErrors({
        ...errors,
        contactName: !formData.contactName ? 'Contact name is required' : null,
        email: !formData.email ? 'Email is required' : null
      });
      return;
    }
    const newContact = {
      id: contacts.length + 1,
      name: formData.contactName,
      email: formData.email,
      phone: formData.phone || '',
      role: formData.role || ''
    };
    setContacts([...contacts, newContact]);
    setFormData({
      ...formData,
      contactName: '',
      email: '',
      phone: '',
      role: ''
    });
  };
  const removeContact = id => {
    setContacts(contacts.filter(contact => contact.id !== id));
  };
  const handleAddAddress = () => {
    if (!formData.street || !formData.city || !formData.addressCountry) {
      setErrors({
        ...errors,
        street: !formData.street ? 'Street is required' : null,
        city: !formData.city ? 'City is required' : null,
        addressCountry: !formData.addressCountry ? 'Country is required' : null
      });
      return;
    }
    const newAddress = {
      id: editingAddress ? editingAddress.id : addresses.length + 1,
      street: formData.street,
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode,
      country: formData.addressCountry,
      addressType: formData.addressType,
      isPrimary: formData.isPrimary
    };
    let updatedAddresses = [];
    if (editingAddress) {
      // Update existing address
      updatedAddresses = addresses.map(addr => addr.id === editingAddress.id ? newAddress : addr);
    } else {
      // Add new address
      // If this is marked as primary, update all others to non-primary
      if (newAddress.isPrimary) {
        updatedAddresses = [...addresses.map(addr => ({
          ...addr,
          isPrimary: false
        })), newAddress];
      } else if (addresses.length === 0) {
        // First address should be primary by default
        updatedAddresses = [{
          ...newAddress,
          isPrimary: true
        }];
      } else {
        updatedAddresses = [...addresses, newAddress];
      }
    }
    setAddresses(updatedAddresses);
    setFormData({
      ...formData,
      street: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      addressType: 'Physical',
      isPrimary: true
    });
    setShowAddressForm(false);
    setEditingAddress(null);
  };
  const handleEditAddress = address => {
    setEditingAddress(address);
    setFormData({
      ...formData,
      street: address.street,
      city: address.city,
      state: address.state || '',
      postalCode: address.postalCode || '',
      addressCountry: address.country,
      addressType: address.addressType || 'Physical',
      isPrimary: address.isPrimary || false
    });
    setShowAddressForm(true);
  };
  const updateAddress = () => {
    if (!formData.street || !formData.city || !formData.addressCountry) {
      setErrors({
        ...errors,
        street: !formData.street ? 'Street is required' : null,
        city: !formData.city ? 'City is required' : null,
        addressCountry: !formData.addressCountry ? 'Country is required' : null
      });
      return;
    }
    const updatedAddress = {
      id: editingAddress.id,
      street: formData.street,
      city: formData.city,
      state: formData.state || '',
      postalCode: formData.postalCode || '',
      country: formData.addressCountry,
      addressType: formData.addressType || 'Physical',
      isPrimary: formData.isPrimary
    };
    // Update existing address
    let updatedAddresses = addresses.map(address => address.id === editingAddress.id ? updatedAddress : address);
    // If this address is now primary, update all others to not be primary
    if (updatedAddress.isPrimary) {
      updatedAddresses = updatedAddresses.map(addr => addr.id === editingAddress.id ? addr : {
        ...addr,
        isPrimary: false
      });
    }
    setAddresses(updatedAddresses);
    // Clear form and exit edit mode
    setFormData({
      ...formData,
      street: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      addressType: 'Physical',
      isPrimary: false
    });
    setEditingAddress(null);
    setShowAddressForm(false);
  };
  const removeAddress = id => {
    const addressToRemove = addresses.find(addr => addr.id === id);
    const wasRemovingPrimary = addressToRemove && addressToRemove.isPrimary;
    const filteredAddresses = addresses.filter(address => address.id !== id);
    // If we removed the primary address and there are other addresses left,
    // make the first one primary
    if (wasRemovingPrimary && filteredAddresses.length > 0) {
      filteredAddresses[0].isPrimary = true;
    }
    setAddresses(filteredAddresses);
  };
  // Bank Account functions
  const handleAddBankAccount = () => {
    if (!formData.bankName || !formData.accountName || !formData.accountNumber) {
      setErrors({
        ...errors,
        bankName: !formData.bankName ? 'Bank name is required' : null,
        accountName: !formData.accountName ? 'Account name is required' : null,
        accountNumber: !formData.accountNumber ? 'Account number is required' : null
      });
      return;
    }
    const newBankAccount = {
      id: editingBankAccount ? editingBankAccount.id : bankAccounts.length + 1,
      bankName: formData.bankName,
      accountName: formData.accountName,
      accountNumber: formData.accountNumber,
      routingNumber: formData.routingNumber,
      swiftCode: formData.swiftCode,
      iban: formData.iban,
      accountType: formData.accountType,
      isPrimary: formData.isPrimary,
      country: formData.addressCountry || formData.country
    };
    let updatedBankAccounts = [];
    if (editingBankAccount) {
      // Update existing bank account
      updatedBankAccounts = bankAccounts.map(account => account.id === editingBankAccount.id ? newBankAccount : account);
    } else {
      // Add new bank account
      // If this is marked as primary, update all others to non-primary
      if (newBankAccount.isPrimary) {
        updatedBankAccounts = [...bankAccounts.map(account => ({
          ...account,
          isPrimary: false
        })), newBankAccount];
      } else if (bankAccounts.length === 0) {
        // First bank account should be primary by default
        updatedBankAccounts = [{
          ...newBankAccount,
          isPrimary: true
        }];
      } else {
        updatedBankAccounts = [...bankAccounts, newBankAccount];
      }
    }
    setBankAccounts(updatedBankAccounts);
    setFormData({
      ...formData,
      bankName: '',
      accountName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      iban: '',
      accountType: 'Checking',
      isPrimary: false
    });
    setShowBankAccountForm(false);
    setEditingBankAccount(null);
  };
  const handleEditBankAccount = account => {
    setEditingBankAccount(account);
    setFormData({
      ...formData,
      bankName: account.bankName,
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      routingNumber: account.routingNumber || '',
      swiftCode: account.swiftCode || '',
      iban: account.iban || '',
      accountType: account.accountType || 'Checking',
      isPrimary: account.isPrimary || false
    });
    setShowBankAccountForm(true);
  };
  const removeBankAccount = id => {
    const accountToRemove = bankAccounts.find(account => account.id === id);
    const wasRemovingPrimary = accountToRemove && accountToRemove.isPrimary;
    const filteredAccounts = bankAccounts.filter(account => account.id !== id);
    // If we removed the primary account and there are other accounts left,
    // make the first one primary
    if (wasRemovingPrimary && filteredAccounts.length > 0) {
      filteredAccounts[0].isPrimary = true;
    }
    setBankAccounts(filteredAccounts);
  };
  const cancelBankAccountEdit = () => {
    setFormData({
      ...formData,
      bankName: '',
      accountName: '',
      accountNumber: '',
      routingNumber: '',
      swiftCode: '',
      iban: '',
      accountType: 'Checking',
      isPrimary: false
    });
    setEditingBankAccount(null);
    setShowBankAccountForm(false);
  };
  const handleFileUpload = newFiles => {
    setFiles([...files, ...newFiles]);
  };
  const removeFile = index => {
    setFiles(files.filter((_, i) => i !== index));
  };
  const validateForm = () => {
    const newErrors = {};
    if (onboardingMethod === 'direct') {
      // Validate required fields for direct onboarding
      if (!formData.name) newErrors.name = 'Vendor name is required';
      if (!formData.type) newErrors.type = 'Vendor type is required';
      if (!formData.country) newErrors.country = 'Country is required';
      if (contacts.length === 0) newErrors.contacts = 'At least one contact is required';
      if (addresses.length === 0) newErrors.addresses = 'At least one address is required';
      // Validate entity selections if not global
      if (!formData.isGlobal && formData.entities.length === 0) {
        newErrors.entities = 'At least one entity must be selected';
      }
      // Validate ESG data if provided
      if (formData.esg && formData.esg.score) {
        const score = parseInt(formData.esg.score);
        if (isNaN(score) || score < 0 || score > 100) {
          newErrors['esg.score'] = 'Score must be a number between 0 and 100';
        }
        if (formData.esg.environmental) {
          const envScore = parseInt(formData.esg.environmental);
          if (isNaN(envScore) || envScore < 0 || envScore > 100) {
            newErrors['esg.environmental'] = 'Score must be a number between 0 and 100';
          }
        }
        if (formData.esg.social) {
          const socialScore = parseInt(formData.esg.social);
          if (isNaN(socialScore) || socialScore < 0 || socialScore > 100) {
            newErrors['esg.social'] = 'Score must be a number between 0 and 100';
          }
        }
        if (formData.esg.governance) {
          const govScore = parseInt(formData.esg.governance);
          if (isNaN(govScore) || govScore < 0 || govScore > 100) {
            newErrors['esg.governance'] = 'Score must be a number between 0 and 100';
          }
        }
      }
    } else {
      // Validate required fields for invite
      if (!inviteData.name) newErrors.name = 'Vendor name is required';
      if (!inviteData.email) newErrors.email = 'Email is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }
    if (onboardingMethod === 'direct') {
      // Prepare data for saving
      const vendorData = {
        ...formData,
        status: 'Active',
        complianceStatus: files.length > 0 ? 'Pending Review' : 'Incomplete',
        contacts,
        addresses,
        bankAccounts,
        documents: files.map((file, index) => ({
          id: index + 1,
          name: file.name,
          type: 'Supporting Document',
          uploadDate: new Date().toISOString().split('T')[0],
          expiryDate: null,
          status: 'Valid'
        })),
        esg: formData.esg.score ? {
          score: parseInt(formData.esg.score),
          environmental: formData.esg.environmental ? parseInt(formData.esg.environmental) : null,
          social: formData.esg.social ? parseInt(formData.esg.social) : null,
          governance: formData.esg.governance ? parseInt(formData.esg.governance) : null,
          summary: formData.esg.summary || '',
          lastUpdated: new Date().toISOString().split('T')[0]
        } : null
      };
      // Call the save handler from parent
      onSave(vendorData);
    } else {
      // Send invitation with availability settings
      const inviteWithAvailability = {
        ...inviteData,
        isGlobal: formData.isGlobal,
        entities: formData.entities,
        sites: formData.sites
      };
      onSendInvite(inviteWithAvailability);
    }
  };
  const cancelAddressEdit = () => {
    setFormData({
      ...formData,
      street: '',
      city: '',
      state: '',
      postalCode: '',
      addressCountry: '',
      addressType: 'Physical',
      isPrimary: false
    });
    setEditingAddress(null);
    setShowAddressForm(false);
  };
  return <div data-id="element-19781">
      <div className="mb-8 flex items-center" data-id="element-19782">
        <button onClick={() => setCurrentView('vendors')} className="mr-4 text-gray-400 hover:text-gray-500" data-id="element-19783">
          <ArrowLeftIcon className="h-5 w-5" data-id="element-19784" />
        </button>
        <div data-id="element-19785">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-19786">
            Add New Vendor
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19787">
            Create a new vendor record or invite a vendor to onboard themselves
          </p>
        </div>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19788">
        <div className="px-4 py-5 sm:px-6" data-id="element-19789">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19790">
            Onboarding Method
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19791">
            Choose how you want to onboard this vendor
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5" data-id="element-19792">
          <div className="flex flex-col sm:flex-row sm:space-x-4" data-id="element-19793">
            <div className={`flex-1 p-4 border rounded-lg mb-4 sm:mb-0 cursor-pointer ${onboardingMethod === 'direct' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`} onClick={() => setOnboardingMethod('direct')} data-id="element-19794">
              <div className="flex items-center" data-id="element-19795">
                <div className="flex-shrink-0" data-id="element-19796">
                  <div className={`h-5 w-5 rounded-full border ${onboardingMethod === 'direct' ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'} flex items-center justify-center`} data-id="element-19797">
                    {onboardingMethod === 'direct' && <CheckIcon className="h-3 w-3 text-white" data-id="element-19798" />}
                  </div>
                </div>
                <div className="ml-3" data-id="element-19799">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-19800">
                    Direct Entry
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-19801">
                    Enter vendor information directly into the system
                  </p>
                </div>
              </div>
            </div>
            <div className={`flex-1 p-4 border rounded-lg cursor-pointer ${onboardingMethod === 'invite' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`} onClick={() => setOnboardingMethod('invite')} data-id="element-19802">
              <div className="flex items-center" data-id="element-19803">
                <div className="flex-shrink-0" data-id="element-19804">
                  <div className={`h-5 w-5 rounded-full border ${onboardingMethod === 'invite' ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'} flex items-center justify-center`} data-id="element-19805">
                    {onboardingMethod === 'invite' && <CheckIcon className="h-3 w-3 text-white" data-id="element-19806" />}
                  </div>
                </div>
                <div className="ml-3" data-id="element-19807">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-19808">
                    Send Invitation
                  </h4>
                  <p className="text-sm text-gray-500" data-id="element-19809">
                    Send an invitation to the vendor to complete their own
                    profile
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {onboardingMethod === 'direct' ? <>
          {/* Basic Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19810">
            <div className="px-4 py-5 sm:px-6" data-id="element-19811">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19812">
                Basic Information
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-19813">
                Enter vendor details and basic information
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-19814">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-19815">
                <div className="sm:col-span-3" data-id="element-19816">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-19817">
                    Vendor Name <span className="text-red-500" data-id="element-19818">*</span>
                  </label>
                  <div className="mt-1" data-id="element-19819">
                    <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.name ? 'border-red-300' : ''}`} data-id="element-19820" />
                    {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-19821">{errors.name}</p>}
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-19822">
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-19823">
                    Vendor Type <span className="text-red-500" data-id="element-19824">*</span>
                  </label>
                  <div className="mt-1" data-id="element-19825">
                    <select id="type" name="type" value={formData.type} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.type ? 'border-red-300' : ''}`} data-id="element-19826">
                      <option value="" data-id="element-19827">Select vendor type</option>
                      {vendorTypes.map(type => <option key={type} value={type} data-id="element-19828">
                          {type}
                        </option>)}
                    </select>
                    {errors.type && <p className="mt-1 text-sm text-red-600" data-id="element-19829">{errors.type}</p>}
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-19830">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-19831">
                    Description
                  </label>
                  <div className="mt-1" data-id="element-19832">
                    <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-19833" />
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-19834">
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-19835">
                    Country <span className="text-red-500" data-id="element-19836">*</span>
                  </label>
                  <div className="mt-1" data-id="element-19837">
                    <select id="country" name="country" value={formData.country} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.country ? 'border-red-300' : ''}`} data-id="element-19838">
                      <option value="" data-id="element-19839">Select country</option>
                      {countries.map(country => <option key={country} value={country} data-id="element-19840">
                          {country}
                        </option>)}
                    </select>
                    {errors.country && <p className="mt-1 text-sm text-red-600" data-id="element-19841">
                        {errors.country}
                      </p>}
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-19842">
                  <label htmlFor="taxId" className="block text-sm font-medium text-gray-700" data-id="element-19843">
                    Tax ID / Business Number
                  </label>
                  <div className="mt-1" data-id="element-19844">
                    <input type="text" name="taxId" id="taxId" value={formData.taxId} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-19845" />
                  </div>
                </div>
                <div className="sm:col-span-6" data-id="element-19846">
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700" data-id="element-19847">
                    Website
                  </label>
                  <div className="mt-1" data-id="element-19848">
                    <input type="url" name="website" id="website" value={formData.website} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-19849" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* ESG Information */}
          {isESGEnabled && hasESGEditPermission && <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19850">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-19851">
                <div data-id="element-19852">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19853">
                    ESG Information
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-19854">
                    Environmental, Social, and Governance data
                  </p>
                </div>
                <button type="button" onClick={() => setShowESGForm(!showESGForm)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19855">
                  {showESGForm ? <>
                      <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-19856" />
                      Hide ESG Form
                    </> : <>
                      <ChevronDownIcon className="h-4 w-4 mr-1" data-id="element-19857" />
                      Add ESG Data
                    </>}
                </button>
              </div>
              {showESGForm && <div className="border-t border-gray-200 px-4 py-5" data-id="element-19858">
                  <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-19859">
                    <div className="sm:col-span-2" data-id="element-19860">
                      <label htmlFor="esg.score" className="block text-sm font-medium text-gray-700" data-id="element-19861">
                        Overall ESG Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-19862">
                        <input type="number" name="esg.score" id="esg.score" min="0" max="100" value={formData.esg.score} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.score'] ? 'border-red-300' : ''}`} data-id="element-19863" />
                        {errors['esg.score'] && <p className="mt-1 text-sm text-red-600" data-id="element-19864">
                            {errors['esg.score']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-2" data-id="element-19865">
                      <label htmlFor="esg.environmental" className="block text-sm font-medium text-gray-700" data-id="element-19866">
                        Environmental Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-19867">
                        <input type="number" name="esg.environmental" id="esg.environmental" min="0" max="100" value={formData.esg.environmental} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.environmental'] ? 'border-red-300' : ''}`} data-id="element-19868" />
                        {errors['esg.environmental'] && <p className="mt-1 text-sm text-red-600" data-id="element-19869">
                            {errors['esg.environmental']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-2" data-id="element-19870">
                      <label htmlFor="esg.social" className="block text-sm font-medium text-gray-700" data-id="element-19871">
                        Social Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-19872">
                        <input type="number" name="esg.social" id="esg.social" min="0" max="100" value={formData.esg.social} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.social'] ? 'border-red-300' : ''}`} data-id="element-19873" />
                        {errors['esg.social'] && <p className="mt-1 text-sm text-red-600" data-id="element-19874">
                            {errors['esg.social']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-3" data-id="element-19875">
                      <label htmlFor="esg.governance" className="block text-sm font-medium text-gray-700" data-id="element-19876">
                        Governance Score (0-100)
                      </label>
                      <div className="mt-1" data-id="element-19877">
                        <input type="number" name="esg.governance" id="esg.governance" min="0" max="100" value={formData.esg.governance} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors['esg.governance'] ? 'border-red-300' : ''}`} data-id="element-19878" />
                        {errors['esg.governance'] && <p className="mt-1 text-sm text-red-600" data-id="element-19879">
                            {errors['esg.governance']}
                          </p>}
                      </div>
                    </div>
                    <div className="sm:col-span-6" data-id="element-19880">
                      <label htmlFor="esg.summary" className="block text-sm font-medium text-gray-700" data-id="element-19881">
                        ESG Summary
                      </label>
                      <div className="mt-1" data-id="element-19882">
                        <textarea id="esg.summary" name="esg.summary" rows={3} value={formData.esg.summary} onChange={handleInputChange} placeholder="Brief summary of vendor's ESG initiatives and performance" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-19883" />
                      </div>
                    </div>
                  </div>
                </div>}
            </div>}
          {/* Contact Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19884">
            <div className="px-4 py-5 sm:px-6" data-id="element-19885">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19886">
                Contact Information
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-19887">
                Add primary and additional contacts for this vendor
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-19888">
              <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden" data-id="element-19889">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-19890">
                  <h4 className="text-base font-medium text-gray-900" data-id="element-19891">
                    Add Contact
                  </h4>
                </div>
                <div className="p-4" data-id="element-19892">
                  <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-19893">
                    <div data-id="element-19894">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19895">
                        Contact Name <span className="text-red-500" data-id="element-19896">*</span>
                      </label>
                      <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.contactName ? 'border-red-300' : ''}`} value={formData.contactName} name="contactName" onChange={handleInputChange} placeholder="Full name" data-id="element-19897" />
                      {errors.contactName && <p className="mt-1 text-sm text-red-600" data-id="element-19898">
                          {errors.contactName}
                        </p>}
                    </div>
                    <div data-id="element-19899">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19900">
                        Email <span className="text-red-500" data-id="element-19901">*</span>
                      </label>
                      <input type="email" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.email ? 'border-red-300' : ''}`} value={formData.email} name="email" onChange={handleInputChange} placeholder="Email address" data-id="element-19902" />
                      {errors.email && <p className="mt-1 text-sm text-red-600" data-id="element-19903">
                          {errors.email}
                        </p>}
                    </div>
                    <div data-id="element-19904">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19905">
                        Phone
                      </label>
                      <input type="tel" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.phone} name="phone" onChange={handleInputChange} placeholder="Phone number" data-id="element-19906" />
                    </div>
                    <div data-id="element-19907">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19908">
                        Role
                      </label>
                      <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.role} name="role" onChange={handleInputChange} placeholder="Job title or role" data-id="element-19909" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-19910">
                    <button type="button" onClick={addContact} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19911">
                      <PlusIcon className="h-4 w-4 mr-1" data-id="element-19912" />
                      Add Contact
                    </button>
                  </div>
                </div>
              </div>
              {errors.contacts && <div className="mb-4" data-id="element-19913">
                  <p className="text-sm text-red-600" data-id="element-19914">{errors.contacts}</p>
                </div>}
              {contacts.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-19915">
                  {contacts.map(contact => <li key={contact.id} className="py-4 flex items-center" data-id="element-19916">
                      <div className="flex-shrink-0" data-id="element-19917">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500" data-id="element-19918">
                          <UserPlusIcon className="h-5 w-5" data-id="element-19919" />
                        </div>
                      </div>
                      <div className="ml-3 flex-1" data-id="element-19920">
                        <p className="text-sm font-medium text-gray-900" data-id="element-19921">
                          {contact.name}
                        </p>
                        <div className="flex items-center text-sm text-gray-500" data-id="element-19922">
                          <MailIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-19923" />
                          {contact.email}
                        </div>
                        {contact.phone && <p className="mt-1 text-sm text-gray-500" data-id="element-19924">
                            {contact.phone}
                          </p>}
                        {contact.role && <p className="mt-1 text-sm text-gray-500" data-id="element-19925">
                            {contact.role}
                          </p>}
                      </div>
                      <div className="ml-4" data-id="element-19926">
                        <button type="button" onClick={() => removeContact(contact.id)} className="text-red-600 hover:text-red-900" data-id="element-19927">
                          <TrashIcon className="h-5 w-5" data-id="element-19928" />
                        </button>
                      </div>
                    </li>)}
                </ul> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-19929">
                  No contacts added yet. Add at least one contact.
                </div>}
            </div>
          </div>
          {/* Address Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19930">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-19931">
              <div data-id="element-19932">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19933">
                  Address Information
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-19934">
                  Add addresses for this vendor
                </p>
              </div>
              <button type="button" onClick={() => setShowAddressForm(!showAddressForm)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19935">
                {showAddressForm ? <>
                    <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-19936" />
                    Hide Address Form
                  </> : <>
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-19937" />
                    Add Address
                  </>}
              </button>
            </div>
            {showAddressForm && <div className="px-4 py-5 border-t border-gray-200" data-id="element-19938">
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-19939">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-19940">
                    <h4 className="text-base font-medium text-gray-900" data-id="element-19941">
                      {editingAddress ? 'Edit Address' : 'Add New Address'}
                    </h4>
                  </div>
                  <div className="p-4" data-id="element-19942">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-19943">
                      <div data-id="element-19944">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19945">
                          Street Address <span className="text-red-500" data-id="element-19946">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.street ? 'border-red-300' : ''}`} value={formData.street} name="street" onChange={handleInputChange} placeholder="Street address" data-id="element-19947" />
                        {errors.street && <p className="mt-1 text-sm text-red-600" data-id="element-19948">
                            {errors.street}
                          </p>}
                      </div>
                      <div data-id="element-19949">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19950">
                          City <span className="text-red-500" data-id="element-19951">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.city ? 'border-red-300' : ''}`} value={formData.city} name="city" onChange={handleInputChange} placeholder="City" data-id="element-19952" />
                        {errors.city && <p className="mt-1 text-sm text-red-600" data-id="element-19953">
                            {errors.city}
                          </p>}
                      </div>
                      <div data-id="element-19954">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19955">
                          State/Province
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.state} name="state" onChange={handleInputChange} placeholder="State or province" data-id="element-19956" />
                      </div>
                      <div data-id="element-19957">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19958">
                          Postal Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.postalCode} name="postalCode" onChange={handleInputChange} placeholder="Postal code" data-id="element-19959" />
                      </div>
                      <div data-id="element-19960">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19961">
                          Country <span className="text-red-500" data-id="element-19962">*</span>
                        </label>
                        <select className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.addressCountry ? 'border-red-300' : ''}`} value={formData.addressCountry} name="addressCountry" onChange={handleInputChange} data-id="element-19963">
                          <option value="" data-id="element-19964">Select country</option>
                          {countries.map(country => <option key={country} value={country} data-id="element-19965">
                              {country}
                            </option>)}
                        </select>
                        {errors.addressCountry && <p className="mt-1 text-sm text-red-600" data-id="element-19966">
                            {errors.addressCountry}
                          </p>}
                      </div>
                      <div data-id="element-19967">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19968">
                          Address Type
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.addressType} name="addressType" onChange={handleInputChange} data-id="element-19969">
                          {addressTypes.map(type => <option key={type} value={type} data-id="element-19970">
                              {type}
                            </option>)}
                        </select>
                      </div>
                      <div className="flex items-center mt-4" data-id="element-19971">
                        <input id="isPrimary" name="isPrimary" type="checkbox" checked={formData.isPrimary} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-19972" />
                        <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-700" data-id="element-19973">
                          Primary Address
                        </label>
                      </div>
                    </div>
                    <div className="mt-4 flex space-x-3" data-id="element-19974">
                      <button type="button" onClick={handleAddAddress} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19975">
                        {editingAddress ? 'Update Address' : 'Add Address'}
                      </button>
                      <button type="button" onClick={() => {
                  setShowAddressForm(false);
                  setEditingAddress(null);
                }} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19976">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>}
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-19977">
              {errors.addresses && <div className="mb-4" data-id="element-19978">
                  <p className="text-sm text-red-600" data-id="element-19979">{errors.addresses}</p>
                </div>}
              {addresses.length > 0 ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-19980">
                  {addresses.map(address => <div key={address.id} className={`relative rounded-lg border ${address.isPrimary ? 'border-indigo-500' : 'border-gray-300'} bg-white px-4 py-3 shadow-sm flex flex-col space-y-2`} data-id="element-19981">
                      {address.isPrimary && <span className="absolute top-0 right-0 -mt-2 -mr-2 px-2 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-800 flex items-center" data-id="element-19982">
                          <CheckIcon className="h-3 w-3 mr-1" data-id="element-19983" />
                          Primary
                        </span>}
                      <div className="flex items-center space-x-3" data-id="element-19984">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-19985">
                          <BuildingIcon className="h-5 w-5 text-gray-500" data-id="element-19986" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-19987">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-19988">
                            {address.addressType}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-19989">
                        <p data-id="element-19990">{address.street}</p>
                        <p data-id="element-19991">
                          {address.city}
                          {address.state && `, ${address.state}`}{' '}
                          {address.postalCode}
                        </p>
                        <p data-id="element-19992">{address.country}</p>
                      </div>
                      <div className="flex justify-end space-x-2 mt-2" data-id="element-19993">
                        <button type="button" onClick={() => handleEditAddress(address)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-19994">
                          Edit
                        </button>
                        <button type="button" onClick={() => removeAddress(address.id)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-19995">
                          Remove
                        </button>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-19996">
                  No addresses added yet. Add at least one address.
                </div>}
            </div>
          </div>
          {/* Bank Account Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-19997">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center" data-id="element-19998">
              <div data-id="element-19999">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20000">
                  Bank Account Information
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-20001">
                  Add payment bank accounts for this vendor
                </p>
              </div>
              <button type="button" onClick={() => setShowBankAccountForm(!showBankAccountForm)} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20002">
                {showBankAccountForm ? <>
                    <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-20003" />
                    Hide Bank Account Form
                  </> : <>
                    <PlusIcon className="h-4 w-4 mr-1" data-id="element-20004" />
                    Add Bank Account
                  </>}
              </button>
            </div>
            {showBankAccountForm && <div className="px-4 py-5 border-t border-gray-200" data-id="element-20005">
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-20006">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200" data-id="element-20007">
                    <h4 className="text-base font-medium text-gray-900" data-id="element-20008">
                      {editingBankAccount ? 'Edit Bank Account' : 'Add New Bank Account'}
                    </h4>
                  </div>
                  <div className="p-4" data-id="element-20009">
                    <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2" data-id="element-20010">
                      <div data-id="element-20011">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20012">
                          Bank Name <span className="text-red-500" data-id="element-20013">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.bankName ? 'border-red-300' : ''}`} value={formData.bankName} name="bankName" onChange={handleInputChange} placeholder="Bank name" data-id="element-20014" />
                        {errors.bankName && <p className="mt-1 text-sm text-red-600" data-id="element-20015">
                            {errors.bankName}
                          </p>}
                      </div>
                      <div data-id="element-20016">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20017">
                          Account Name <span className="text-red-500" data-id="element-20018">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.accountName ? 'border-red-300' : ''}`} value={formData.accountName} name="accountName" onChange={handleInputChange} placeholder="Account holder name" data-id="element-20019" />
                        {errors.accountName && <p className="mt-1 text-sm text-red-600" data-id="element-20020">
                            {errors.accountName}
                          </p>}
                      </div>
                      <div data-id="element-20021">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20022">
                          Account Number <span className="text-red-500" data-id="element-20023">*</span>
                        </label>
                        <input type="text" className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.accountNumber ? 'border-red-300' : ''}`} value={formData.accountNumber} name="accountNumber" onChange={handleInputChange} placeholder="Account number" data-id="element-20024" />
                        {errors.accountNumber && <p className="mt-1 text-sm text-red-600" data-id="element-20025">
                            {errors.accountNumber}
                          </p>}
                      </div>
                      <div data-id="element-20026">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20027">
                          Routing/BSB/Sort Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.routingNumber} name="routingNumber" onChange={handleInputChange} placeholder="Routing/BSB/Sort code" data-id="element-20028" />
                      </div>
                      <div data-id="element-20029">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20030">
                          SWIFT/BIC Code
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.swiftCode} name="swiftCode" onChange={handleInputChange} placeholder="SWIFT/BIC code" data-id="element-20031" />
                      </div>
                      <div data-id="element-20032">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20033">
                          IBAN
                        </label>
                        <input type="text" className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.iban} name="iban" onChange={handleInputChange} placeholder="IBAN (if applicable)" data-id="element-20034" />
                      </div>
                      <div data-id="element-20035">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20036">
                          Account Type
                        </label>
                        <select className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" value={formData.accountType} name="accountType" onChange={handleInputChange} data-id="element-20037">
                          {accountTypes.map(type => <option key={type} value={type} data-id="element-20038">
                              {type}
                            </option>)}
                        </select>
                      </div>
                      <div className="flex items-center mt-4" data-id="element-20039">
                        <input id="isPrimary" name="isPrimary" type="checkbox" checked={formData.isPrimary} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-20040" />
                        <label htmlFor="isPrimary" className="ml-2 block text-sm text-gray-700" data-id="element-20041">
                          Primary Bank Account
                        </label>
                      </div>
                    </div>
                    <div className="mt-4 flex space-x-3" data-id="element-20042">
                      <button type="button" onClick={handleAddBankAccount} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20043">
                        {editingBankAccount ? 'Update Bank Account' : 'Add Bank Account'}
                      </button>
                      <button type="button" onClick={cancelBankAccountEdit} className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20044">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>}
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-20045">
              {bankAccounts.length > 0 ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-20046">
                  {bankAccounts.map(account => <div key={account.id} className={`relative rounded-lg border ${account.isPrimary ? 'border-indigo-500' : 'border-gray-300'} bg-white px-4 py-3 shadow-sm flex flex-col space-y-2`} data-id="element-20047">
                      {account.isPrimary && <span className="absolute top-0 right-0 -mt-2 -mr-2 px-2 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-800 flex items-center" data-id="element-20048">
                          <CheckIcon className="h-3 w-3 mr-1" data-id="element-20049" />
                          Primary
                        </span>}
                      <div className="flex items-center space-x-3" data-id="element-20050">
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-20051">
                          <div className="h-5 w-5 text-gray-500" data-id="element-20052" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-20053">
                          <p className="text-sm font-medium text-gray-900 truncate" data-id="element-20054">
                            {account.bankName}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-20055">
                        <p data-id="element-20056">Account Name: {account.accountName}</p>
                        <p data-id="element-20057">
                          Account Number: •••• {account.accountNumber.slice(-4)}
                        </p>
                        <p data-id="element-20058">Account Type: {account.accountType}</p>
                        {account.routingNumber && <p data-id="element-20059">Routing: {account.routingNumber}</p>}
                      </div>
                      <div className="flex justify-end space-x-2 mt-2" data-id="element-20060">
                        <button type="button" onClick={() => handleEditBankAccount(account)} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-20061">
                          Edit
                        </button>
                        <button type="button" onClick={() => removeBankAccount(account.id)} className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-20062">
                          Remove
                        </button>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-4 text-sm text-gray-500" data-id="element-20063">
                  No bank accounts added yet. Add at least one bank account for
                  payment processing.
                </div>}
            </div>
          </div>
          {/* Entity Access */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-20064">
            <div className="px-4 py-5 sm:px-6" data-id="element-20065">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20066">
                Entity Access
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-20067">
                Define which entities can use this vendor
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-20068">
              <div className="flex items-center mb-4" data-id="element-20069">
                <input id="isGlobal" name="isGlobal" type="checkbox" checked={formData.isGlobal} onChange={handleInputChange} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-20070" />
                <label htmlFor="isGlobal" className="ml-2 block text-sm text-gray-700" data-id="element-20071">
                  Global Vendor (available to all entities)
                </label>
              </div>
              {!formData.isGlobal && <div className="mt-4" data-id="element-20072">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20073">
                    Select Entities
                  </label>
                  <select multiple className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border h-32" value={formData.entities} onChange={e => {
              const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
              setFormData({
                ...formData,
                entities: selectedOptions
              });
            }} data-id="element-20074">
                    {entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-20075">
                        {entity.name}
                      </option>)}
                  </select>
                  <p className="mt-1 text-xs text-gray-500" data-id="element-20076">
                    Hold Ctrl/Cmd to select multiple entities
                  </p>
                </div>}
            </div>
          </div>
          {/* Documents */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-20077">
            <div className="px-4 py-5 sm:px-6" data-id="element-20078">
              <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20079">
                Documents
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-20080">
                Upload supporting documents
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5" data-id="element-20081">
              <FileUpload onFileUpload={handleFileUpload} data-id="element-20082" />
              {files.length > 0 && <div className="mt-4" data-id="element-20083">
                  <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-20084">
                    Files to Upload
                  </h4>
                  <ul className="border rounded-md divide-y divide-gray-200" data-id="element-20085">
                    {files.map((file, index) => <li key={index} className="pl-3 pr-4 py-3 flex items-center justify-between text-sm" data-id="element-20086">
                        <div className="w-0 flex-1 flex items-center" data-id="element-20087">
                          <svg className="flex-shrink-0 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-20088">
                            <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" data-id="element-20089" />
                          </svg>
                          <span className="ml-2 flex-1 w-0 truncate" data-id="element-20090">
                            {file.name}
                          </span>
                        </div>
                        <div className="ml-4 flex-shrink-0" data-id="element-20091">
                          <button type="button" onClick={() => removeFile(index)} className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-20092">
                            Remove
                          </button>
                        </div>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>
          {/* Submit Button */}
          <div className="flex justify-end" data-id="element-20093">
            <button type="button" onClick={() => setCurrentView('vendors')} className="mr-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20094">
              Cancel
            </button>
            <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20095">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-20096" />
              Save Vendor
            </button>
          </div>
        </> :
    // Send Invitation Form
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-20097">
          <div className="px-4 py-5 sm:px-6" data-id="element-20098">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-20099">
              Send Vendor Invitation
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-20100">
              Invite a vendor to complete their own onboarding
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5" data-id="element-20101">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-20102">
              <div className="sm:col-span-3" data-id="element-20103">
                <label htmlFor="inviteName" className="block text-sm font-medium text-gray-700" data-id="element-20104">
                  Vendor Name <span className="text-red-500" data-id="element-20105">*</span>
                </label>
                <div className="mt-1" data-id="element-20106">
                  <input type="text" name="name" id="inviteName" value={inviteData.name} onChange={handleInviteInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.inviteName ? 'border-red-300' : ''}`} data-id="element-20107" />
                  {errors.inviteName && <p className="mt-1 text-sm text-red-600" data-id="element-20108">
                      {errors.inviteName}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-20109">
                <label htmlFor="inviteType" className="block text-sm font-medium text-gray-700" data-id="element-20110">
                  Vendor Type
                </label>
                <div className="mt-1" data-id="element-20111">
                  <select id="inviteType" name="type" value={inviteData.type} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-20112">
                    <option value="" data-id="element-20113">Select vendor type</option>
                    {vendorTypes.map(type => <option key={type} value={type} data-id="element-20114">
                        {type}
                      </option>)}
                  </select>
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-20115">
                <label htmlFor="inviteContactName" className="block text-sm font-medium text-gray-700" data-id="element-20116">
                  Contact Name <span className="text-red-500" data-id="element-20117">*</span>
                </label>
                <div className="mt-1" data-id="element-20118">
                  <input type="text" name="contactName" id="inviteContactName" value={inviteData.contactName} onChange={handleInviteInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.inviteContactName ? 'border-red-300' : ''}`} data-id="element-20119" />
                  {errors.inviteContactName && <p className="mt-1 text-sm text-red-600" data-id="element-20120">
                      {errors.inviteContactName}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-20121">
                <label htmlFor="inviteEmail" className="block text-sm font-medium text-gray-700" data-id="element-20122">
                  Email <span className="text-red-500" data-id="element-20123">*</span>
                </label>
                <div className="mt-1" data-id="element-20124">
                  <input type="email" name="email" id="inviteEmail" value={inviteData.email} onChange={handleInviteInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border ${errors.inviteEmail ? 'border-red-300' : ''}`} data-id="element-20125" />
                  {errors.inviteEmail && <p className="mt-1 text-sm text-red-600" data-id="element-20126">
                      {errors.inviteEmail}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-20127">
                <label htmlFor="invitePhone" className="block text-sm font-medium text-gray-700" data-id="element-20128">
                  Phone
                </label>
                <div className="mt-1" data-id="element-20129">
                  <input type="tel" name="phone" id="invitePhone" value={inviteData.phone} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-20130" />
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-20131">
                <label htmlFor="inviteRole" className="block text-sm font-medium text-gray-700" data-id="element-20132">
                  Role
                </label>
                <div className="mt-1" data-id="element-20133">
                  <input type="text" name="role" id="inviteRole" value={inviteData.role} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-20134" />
                </div>
              </div>
              <div className="sm:col-span-6" data-id="element-20135">
                <label htmlFor="inviteMessage" className="block text-sm font-medium text-gray-700" data-id="element-20136">
                  Invitation Message
                </label>
                <div className="mt-1" data-id="element-20137">
                  <textarea id="inviteMessage" name="message" rows={4} value={inviteData.message} onChange={handleInviteInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border" data-id="element-20138" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end" data-id="element-20139">
              <button type="button" onClick={() => setCurrentView('vendors')} className="mr-3 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20140">
                Cancel
              </button>
              <button type="button" onClick={handleSubmit} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20141">
                <UserPlusIcon className="h-4 w-4 mr-2" data-id="element-20142" />
                Send Invitation
              </button>
            </div>
          </div>
        </div>}
    </div>;
}